import { HTMLText, Text } from 'pixi.js';
import type { Container } from 'pixi.js';
import { Signal } from 'typed-signals';
import type { Popup } from '../../popups/Popup';
import type { IAPProductWithPopupData } from '../../app/iap/types';
import { playLayoutAnimation } from '../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { formatInventoryCountdown } from '../shopPage/utils/setIconForInventory';
import { attachIntervalToDisplayObject } from '../../utils/time-utils';
import {
  createLayoutInstanceFromPath,
  createLayoutInstanceFromRaw,
} from '../../libs/layouts/runtime/createLayoutInstance';
import type { SpecialOfferLayout } from '../../libs/layouts/types/LayoutShapes';

// Uses centralized SpecialOfferLayout from LayoutShapes

export class SpecialOfferPopup implements Popup {
  public readonly onBuyClicked = new Signal();

  public readonly onCloseClicked = new Signal();

  public readonly layout: SpecialOfferLayout;

  public readonly root: Container;

  constructor(private readonly product: IAPProductWithPopupData) {
    const { popupLayout } = product.data;
    if (popupLayout?.raw) {
      this.layout = createLayoutInstanceFromRaw<SpecialOfferLayout>(popupLayout.raw);
    } else if (popupLayout?.path) {
      this.layout = createLayoutInstanceFromPath<SpecialOfferLayout>(popupLayout.path);
    } else {
      throw new Error('SpecialOfferPopup requires a popupLayout (path or raw)');
    }
    this.root = this.layout.root;
    void this.init(product);
  }

  private async init(product: IAPProductWithPopupData): Promise<void> {
    this.injectPrice(product.priceLabel);

    const { buyButton, closeButton, timerText } = this.layout;

    buyButton?.root?.on('pointertap', () => {
      this.onBuyClicked.emit();
    });

    closeButton?.on('pointertap', () => {
      this.onCloseClicked.emit();
    });

    if (timerText) {
      attachIntervalToDisplayObject(timerText, () => {
        formatInventoryCountdown(timerText, this.product.endDate ?? 0);
        if ((this.product.endDate ?? 0) < Date.now()) this.onCloseClicked.emit();
      });
    }

    await this.playSafe('Appear');
    void this.playSafe('Idle');
  }

  private async playSafe(name: string): Promise<void> {
    const ctor = this.layout.constructor as unknown as { raw?: { animations?: { name: string }[] } };
    const hasAnimation = (ctor.raw?.animations ?? []).some((a) => a.name === name);
    if (!hasAnimation) {
      if (name === 'Appear') await this.root.tweenFrom({ alpha: 0 }, 300, 'quadOut', true).asyncOnComplete();
      if (name === 'Disappear') await this.root.tweenTo({ alpha: 0 }, 300, 'quadOut', true).asyncOnComplete();
      return;
    }
    await playLayoutAnimation(this.layout, name);
  }

  private injectPrice(priceLabel: string): void {
    const layoutAny = this.layout as unknown as Record<string, unknown>;
    Object.values(layoutAny)
      .filter((v): v is Text | HTMLText => v instanceof Text || v instanceof HTMLText)
      .forEach((textObj) => {
        textObj.text = textObj.text.replace('<price>', priceLabel);
      });
  }
}
